// ClienteIntakeHome — the cliente's landing screen after the last-4 login.
//
// Five routes into the same destination: a desarrollos_intake_events row on the
// cliente's live Anexo. Captures and uploads are real; only the extraction is
// mocked (see mockExtraction.js). Nothing here writes desarrollos_anexo_items —
// extracted lines stay in extracted_fields until the operator promotes them.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Camera,
  FileText,
  Mic,
  Pencil,
  MessageCircle,
  Check,
  Loader2,
  Square,
  X,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import { useCliente } from './ClienteContext';
import { buildExtraction, loadCatalog, VOICE_TRANSCRIPT } from './mockExtraction';
import SivarSendWordmark from '../../components/brand/SivarSendWordmark';
import ClienteActions from './ClienteActions';
import { sanitizeFileName, uploadWithProgress } from './uploadWithProgress';

const BUCKETS = {
  image: 'desarrollos-images',
  factura: 'desarrollos-facturas',
  voice: 'desarrollos-voice',
};

const PERFIL_GATE_COPY = {
  missing: {
    title: 'Antes de empezar, configurá tu perfil aduanero',
    body: 'Necesitamos tus datos de gestor y aduana una sola vez. Después de eso, mandás tus datos como quieras y nosotros armamos el Anexo.',
  },
  'pendiente_de_activación': {
    title: 'Tu perfil está pendiente de activación',
    body: 'Ya recibimos tus datos. Un operador los está revisando — apenas quede activado vas a poder empezar a mandarnos tus envíos.',
  },
  'revisión_requerida': {
    title: 'Tu perfil necesita una corrección',
    body: 'Hay un dato que debemos ajustar antes de activarte. Entrá a tu perfil para ver qué falta y corregirlo.',
  },
};

const mmss = (totalSeconds) => {
  const m = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${m}:${s}`;
};

// ── Presentational bits ─────────────────────────────────────────────────────

const UsageChip = ({ count }) =>
  count > 0 ? (
    <span className="absolute bottom-4 right-4 text-[10px] font-bold uppercase px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">
      {count === 1 ? 'Usado 1 vez' : `Usado ${count} veces`}
    </span>
  ) : null;

const RouteCard = ({ icon: Icon, title, description, onClick, children, chip, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="relative w-full text-left bg-white border border-slate-200 rounded-2xl p-5 pb-12 min-h-[132px] transition-colors hover:border-slate-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand-teal)] disabled:opacity-70"
  >
    <Icon size={28} className="text-slate-900" aria-hidden="true" />
    <p className="mt-3 text-sm font-bold text-slate-900">{title}</p>
    <p className="mt-1 text-xs text-slate-500 leading-relaxed">{description}</p>
    {children}
    {chip}
  </button>
);

const CardProgress = ({ pct }) => (
  <div className="mt-3">
    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full bg-[var(--brand-teal)] transition-all duration-200" style={{ width: `${pct}%` }} />
    </div>
    <p className="mt-1.5 text-[10px] font-semibold text-slate-500">Subiendo… {pct}%</p>
  </div>
);

const CardError = ({ message, onRetry }) => (
  <div className="mt-3 flex items-start gap-2">
    <AlertCircle size={13} className="text-red-600 mt-0.5 shrink-0" />
    <div>
      <p className="text-[11px] text-red-600 leading-snug">{message}</p>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onRetry();
          }
        }}
        className="inline-block mt-1 text-[11px] font-bold text-slate-700 underline cursor-pointer"
      >
        Reintentar
      </span>
    </div>
  </div>
);

// CSS-only decorative waveform — not audio analysis, and not presented as such.
const Waveform = () => (
  <div className="flex items-end gap-1 h-6" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className="w-1 bg-[var(--brand-teal)] rounded-full animate-pulse"
        style={{
          height: `${[40, 75, 100, 60, 30][i]}%`,
          animationDelay: `${i * 120}ms`,
          animationDuration: '900ms',
        }}
      />
    ))}
  </div>
);

// ── Main screen ─────────────────────────────────────────────────────────────

const ClienteIntakeHome = ({ onNavigate }) => {
  const { cliente } = useCliente();

  const [gate, setGate] = useState('loading'); // loading | blocked | ready | error
  const [perfilEstado, setPerfilEstado] = useState(null);
  const [anexoId, setAnexoId] = useState(null);
  const [anexoExistedOnMount, setAnexoExistedOnMount] = useState(false);
  const [usage, setUsage] = useState({});
  const [busy, setBusy] = useState(null); // which card is working
  const [progress, setProgress] = useState(0);
  const [cardError, setCardError] = useState({});
  const [result, setResult] = useState(null); // { stage, count }
  const [waModal, setWaModal] = useState(false);

  // Voice
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const imageInputRef = useRef(null);
  const facturaInputRef = useRef(null);
  const retryRef = useRef(null);

  // ── Mount: perfil gate, then live Anexo, then usage counts ───────────────
  const loadUsage = useCallback(async (id) => {
    if (!id) return;
    const { data, error } = await supabase
      .from('desarrollos_intake_events')
      .select('source_type')
      .eq('anexo_id', id);
    if (error) {
      // Non-fatal: usage chips are decoration, the routes still work.
      console.error('[Desarrollos/intake] no se pudo contar los eventos', error);
      return;
    }
    const counts = {};
    (data || []).forEach((e) => {
      counts[e.source_type] = (counts[e.source_type] || 0) + 1;
    });
    setUsage(counts);
  }, []);

  useEffect(() => {
    if (!cliente) {
      onNavigate('/desarrollos');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data: perfil, error: perfilError } = await supabase
          .from('desarrollos_perfil_aduanero')
          .select('estado')
          .eq('client_id', cliente.id)
          .maybeSingle();
        if (perfilError) throw perfilError;
        if (cancelled) return;

        const estado = perfil?.estado || null;
        setPerfilEstado(estado);

        if (estado !== 'activado') {
          setGate('blocked');
          return;
        }

        const { data: anexo, error: anexoError } = await supabase
          .from('desarrollos_anexos')
          .select('id, updated_at')
          .eq('client_id', cliente.id)
          .eq('status', 'live')
          .limit(1)
          .maybeSingle();
        if (anexoError) throw anexoError;
        if (cancelled) return;

        if (anexo?.id) {
          setAnexoId(anexo.id);
          setAnexoExistedOnMount(true);
          loadUsage(anexo.id);
        }

        loadCatalog(); // warm the mock-extraction pool before first capture
        setGate('ready');
      } catch (err) {
        console.error('[Desarrollos/intake] error cargando el estado del cliente', err);
        if (!cancelled) setGate('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cliente, onNavigate, loadUsage]);

  // Stop the mic if the screen unmounts mid-recording.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    },
    []
  );

  // ── Lazy Anexo creation ──────────────────────────────────────────────────
  // Called only after a capture has actually landed, so an empty Anexo is
  // never created. The partial unique index idx_anexos_one_live_per_client
  // makes a concurrent second insert fail with 23505 — treat that as "someone
  // else won the race" and adopt the existing row.
  const ensureAnexo = useCallback(async () => {
    if (anexoId) return anexoId;

    const { data, error } = await supabase
      .from('desarrollos_anexos')
      .insert([
        {
          client_id: cliente.id,
          tenant_id: DESARROLLOS_TENANT_ID,
          status: 'live',
          price_usd: 10.0,
        },
      ])
      .select('id')
      .single();

    if (error) {
      if (error.code === '23505') {
        const { data: existing, error: reread } = await supabase
          .from('desarrollos_anexos')
          .select('id')
          .eq('client_id', cliente.id)
          .eq('status', 'live')
          .limit(1)
          .maybeSingle();
        if (reread || !existing?.id) throw reread || error;
        setAnexoId(existing.id);
        return existing.id;
      }
      throw error;
    }

    setAnexoId(data.id);
    return data.id;
  }, [anexoId, cliente]);

  // ── Shared capture pipeline ──────────────────────────────────────────────
  const runCapture = useCallback(
    async ({ sourceType, file, transcript }) => {
      setCardError((prev) => ({ ...prev, [sourceType]: null }));
      setBusy(sourceType);
      setProgress(0);

      try {
        // Upload first. If it fails we have created nothing.
        const folder = anexoId || 'pending';
        const path = `${cliente.id}/${folder}/${Date.now()}-${sanitizeFileName(file.name)}`;
        const storagePath = await uploadWithProgress({
          bucket: BUCKETS[sourceType],
          path,
          file,
          onProgress: setProgress,
        });

        // Only now does the Anexo become real.
        const targetAnexoId = await ensureAnexo();
        const extracted = await buildExtraction(sourceType);

        const { error: insertError } = await supabase.from('desarrollos_intake_events').insert([
          {
            anexo_id: targetAnexoId,
            tenant_id: DESARROLLOS_TENANT_ID,
            source_type: sourceType,
            raw_payload_url: storagePath,
            raw_transcript: transcript || null,
            extracted_fields: extracted,
            confidence_score: 0.84,
            needs_review: true,
          },
        ]);
        if (insertError) throw insertError;

        setUsage((prev) => ({ ...prev, [sourceType]: (prev[sourceType] || 0) + 1 }));
        setResult({ stage: 'reading', count: extracted.items.length });
        loadUsage(targetAnexoId);
      } catch (err) {
        console.error(`[Desarrollos/intake] fallo la captura ${sourceType}`, err);
        setCardError((prev) => ({
          ...prev,
          [sourceType]: 'No pudimos enviar esto. Revisá tu señal y probá de nuevo.',
        }));
      } finally {
        setBusy(null);
        setProgress(0);
      }
    },
    [anexoId, cliente, ensureAnexo, loadUsage]
  );

  // Mocked "reading" delay, then the resolved state.
  useEffect(() => {
    if (result?.stage !== 'reading') return undefined;
    const t = setTimeout(() => setResult((r) => (r ? { ...r, stage: 'done' } : null)), 2000);
    return () => clearTimeout(t);
  }, [result?.stage]);

  const onFilePicked = (sourceType) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked twice
    if (!file) return;
    retryRef.current = () =>
      sourceType === 'image' ? imageInputRef.current?.click() : facturaInputRef.current?.click();
    runCapture({ sourceType, file });
  };

  // ── Voice ────────────────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    setCardError((prev) => ({ ...prev, voice: null }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Safari produces mp4; Chrome/Firefox/Edge produce webm.
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const ext = mime === 'audio/webm' ? 'webm' : 'mp4';
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size === 0) return;

        const file = new File([blob], `nota-de-voz.${ext}`, { type: mime });
        retryRef.current = startRecording;
        runCapture({ sourceType: 'voice', file, transcript: VOICE_TRANSCRIPT });
      };

      recorder.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      console.error('[Desarrollos/intake] no se pudo acceder al micrófono', err);
      setCardError((prev) => ({
        ...prev,
        voice: 'No pudimos usar el micrófono. Revisá los permisos del navegador.',
      }));
    }
  }, [runCapture]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (!cliente) return null;

  if (gate === 'loading') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-5">
        <div className="max-w-3xl mx-auto space-y-4 pt-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 rounded-2xl bg-white border border-slate-200 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const header = (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4">
        <SivarSendWordmark size="md" />
        <ClienteActions onNavigate={onNavigate} />
      </div>
      <p className="mt-2 text-sm text-slate-500">
        Hola {cliente.full_name?.split(' ')[0] || cliente.id}, ¿cómo querés mandarnos tus datos?
      </p>
    </div>
  );

  if (gate === 'error') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-5">
        <div className="max-w-3xl mx-auto pt-6">
          {header}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-sm text-red-600">
              No pudimos cargar tu información. Revisá tu conexión y volvé a intentar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Perfil gate — renders INSTEAD of the routes, never alongside them.
  if (gate === 'blocked') {
    const copy = PERFIL_GATE_COPY[perfilEstado] || PERFIL_GATE_COPY.missing;
    return (
      <div className="min-h-screen bg-[#f8fafc] p-5">
        <div className="max-w-3xl mx-auto pt-6">
          {header}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-sm font-bold text-slate-900">{copy.title}</p>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">{copy.body}</p>
            <button
              type="button"
              onClick={() => onNavigate('/desarrollos/cliente/perfil')}
              className="mt-5 w-full sm:w-auto min-h-[44px] px-5 bg-[var(--brand-teal)] hover:brightness-[1.08] text-white font-semibold text-sm rounded-lg transition"
            >
              Configurar mi perfil aduanero
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-5">
      <div className="max-w-3xl mx-auto pt-6">
        {/* Live-Anexo bar */}
        {anexoExistedOnMount && anexoId && (
          <button
            type="button"
            onClick={() => onNavigate('/desarrollos/cliente/mi-anexo')}
            className="w-full mb-5 min-h-[44px] flex items-center justify-between gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl text-left hover:border-slate-300 transition-colors"
          >
            <span className="text-xs font-semibold text-slate-700">Ya tenés un Anexo en curso</span>
            <span className="flex items-center gap-1 text-xs font-bold text-[var(--brand-teal)] shrink-0">
              Ver detalles <ArrowRight size={13} />
            </span>
          </button>
        )}

        {header}

        {/* Result card — pushes the routes down, never replaces them */}
        {result && (
          <div className="mb-5 bg-white border border-slate-200 rounded-2xl p-5">
            {result.stage === 'reading' ? (
              <div className="flex items-center gap-2.5">
                <Loader2 size={16} className="animate-spin text-slate-400" />
                <p className="text-sm text-slate-600">Datos recibidos. Los estamos leyendo…</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-green-50 border border-green-200 flex items-center justify-center shrink-0">
                    <Check size={13} className="text-green-700" />
                  </span>
                  <p className="text-sm font-semibold text-slate-900">
                    Encontramos {result.count} {result.count === 1 ? 'artículo' : 'artículos'}
                  </p>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => onNavigate('/desarrollos/cliente/mi-anexo')}
                    className="min-h-[44px] px-4 bg-[var(--brand-teal)] hover:brightness-[1.08] text-white font-semibold text-sm rounded-lg transition"
                  >
                    Ver mi Anexo
                  </button>
                  <button
                    type="button"
                    onClick={() => setResult(null)}
                    className="min-h-[44px] px-4 border border-slate-200 text-slate-600 font-semibold text-sm rounded-lg hover:bg-slate-50 transition"
                  >
                    Enviar más datos
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Hidden native inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onFilePicked('image')}
        />
        <input
          ref={facturaInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={onFilePicked('factura')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1 — Foto */}
          <RouteCard
            icon={Camera}
            title="Foto de recibo o producto"
            description="Sacale una foto a tu recibo o al producto. Nosotros extraemos los datos."
            onClick={() => imageInputRef.current?.click()}
            disabled={busy === 'image'}
            chip={<UsageChip count={usage.image || 0} />}
          >
            {busy === 'image' && <CardProgress pct={progress} />}
            {cardError.image && (
              <CardError message={cardError.image} onRetry={() => imageInputRef.current?.click()} />
            )}
          </RouteCard>

          {/* 2 — Factura */}
          <RouteCard
            icon={FileText}
            title="Factura completa (PDF o foto)"
            description="Subí tu factura de compra. Sacamos línea por línea."
            onClick={() => facturaInputRef.current?.click()}
            disabled={busy === 'factura'}
            chip={<UsageChip count={usage.factura || 0} />}
          >
            {busy === 'factura' && <CardProgress pct={progress} />}
            {cardError.factura && (
              <CardError
                message={cardError.factura}
                onRetry={() => facturaInputRef.current?.click()}
              />
            )}
          </RouteCard>

          {/* 3 — Voz */}
          <div className="relative bg-white border border-slate-200 rounded-2xl p-5 pb-12 min-h-[132px]">
            {recording ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[var(--brand-teal)] animate-pulse" />
                    <span className="text-sm font-bold text-slate-900 tabular-nums">
                      {mmss(seconds)}
                    </span>
                  </div>
                  <Waveform />
                </div>
                <p className="mt-3 text-xs text-slate-500">Grabando… contanos qué vas a mandar.</p>
                <button
                  type="button"
                  onClick={stopRecording}
                  className="mt-3 min-h-[44px] w-full flex items-center justify-center gap-2 bg-[var(--brand-teal)] hover:brightness-[1.08] text-white font-semibold text-sm rounded-lg transition"
                >
                  <Square size={13} /> Detener y enviar
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={startRecording}
                disabled={busy === 'voice'}
                className="w-full text-left disabled:opacity-70"
              >
                <Mic size={28} className="text-slate-900" aria-hidden="true" />
                <p className="mt-3 text-sm font-bold text-slate-900">Nota de voz</p>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                  Decinos qué mandás — un shampoo, dos camisas, un cargador. Lo transcribimos.
                </p>
              </button>
            )}
            {busy === 'voice' && <CardProgress pct={progress} />}
            {cardError.voice && <CardError message={cardError.voice} onRetry={startRecording} />}
            <UsageChip count={usage.voice || 0} />
          </div>

          {/* 4 — Manual */}
          <RouteCard
            icon={Pencil}
            title="Escribirlo yo mismo"
            description="Formulario clásico — vos escribís cada línea."
            onClick={() => onNavigate('/desarrollos/cliente/intake-manual')}
            chip={<UsageChip count={usage.manual || 0} />}
          />

          {/* 5 — WhatsApp */}
          <RouteCard
            icon={MessageCircle}
            title="WhatsApp"
            description="Mandanos tus datos por WhatsApp. Nuestro agente los procesa."
            onClick={() => setWaModal(true)}
            chip={
              <span className="absolute bottom-4 right-4 text-[10px] font-bold uppercase px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                Próximamente
              </span>
            }
          />
        </div>
      </div>

      {waModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-5">
          <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-3">
              <MessageCircle size={22} className="text-slate-900" />
              <button type="button" onClick={() => setWaModal(false)} aria-label="Cerrar">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Estamos preparando esto. Vas a poder mandarnos todo por WhatsApp muy pronto. Por
              ahora, usá cualquiera de los otros métodos.
            </p>
            <button
              type="button"
              onClick={() => setWaModal(false)}
              className="mt-5 w-full min-h-[44px] border border-slate-200 text-slate-600 font-semibold text-sm rounded-lg hover:bg-slate-50 transition"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteIntakeHome;
